import { CdkVirtualScrollViewport, VIRTUAL_SCROLL_STRATEGY, VirtualScrollStrategy } from '@angular/cdk/scrolling';
import { Directive, ElementRef, Injectable, Input, forwardRef } from '@angular/core';
import { Observable, Subject, distinctUntilChanged } from 'rxjs';


interface ItemHeight {
  value: number;
  // 'stale': a real measurement kept for layout but re-measured on the next pass.
  source: 'predicted' | 'actual' | 'stale';
}

interface VariableItem {
  id: string;
  data: any;
}

/**
 * Normalizes a height-cache key.
 *
 * Predictions are keyed from the item list, measurements from the
 * `[variable-item]` input — and callers bind that to a numeric index. `Map` does
 * not treat `0` and `'0'` as the same key, so the two sides used to write to
 * separate entries: every measured height landed somewhere nothing read, the
 * total content size stayed the sum of the *predictions* forever, and their
 * accumulated error showed up as blank scroll area past the last item.
 */
function cacheKeyFor(id: unknown, fallbackIndex: number): string {
  return id === undefined || id === null || id === '' ? fallbackIndex.toString() : String(id);
}

@Injectable()
export class VariableVirtualScrollStrategy implements VirtualScrollStrategy {
  items: VariableItem[] = [];
  elements: VariableVirtualScrollItemDirective[];
  viewport: CdkVirtualScrollViewport;
  heightCache = new Map<string, ItemHeight>();
  above = 10;
  below = 10;
  private pendingMeasure = false;
  private skipNextCarry = false;

  scrolledIndexChange$ = new Subject<number>();
  scrolledIndexChange: Observable<number> = this.scrolledIndexChange$.pipe(distinctUntilChanged());

  constructor(public predictor: ItemSizePredictor) {}

  attach(viewport: CdkVirtualScrollViewport): void {
    this.viewport = viewport;

    if (this.items) {
      this.viewport.setTotalContentSize(this.getTotalHeight());
      this.updateRenderedRange();
    }
  }

  detach(): void {
    this.viewport = null;
  }

  onContentScrolled(): void {
    if (this.viewport) {
      this.updateRenderedRange();
    }
  }

  onDataLengthChanged(): void {
    if (!this.viewport) {
      return;
    }

    this.viewport.setTotalContentSize(this.getTotalHeight());
    this.updateRenderedRange();
  }

  onContentRendered(): void {
     /** no-op */
  }

  onRenderedOffsetChanged(): void {
     /** no-op */
  }

  scrollToIndex(index: number, behavior: ScrollBehavior): void {
    if (!this.viewport) {
      return;
    }

    const offset = this.getOffsetByItemId(index);
    this.viewport.scrollToOffset(offset, behavior);
  }

  updateItems(items: any[]) {
    // Pair each item with its cache id rather than spreading the id into a copy
    // of it: object spread only carries own enumerable properties, so a class
    // instance reached the predictor stripped of everything defined on its
    // prototype. `MatTableDataSource.data` is a prototype getter, which made
    // every card fall back to `minHeight` and broke the total content size.
    // Carry measured heights over by data reference — dropping them reverts the total to predictions and bounces the viewport.
    const measuredByData = new Map<any, ItemHeight>();
    if (!this.skipNextCarry) {
      for (const item of this.items) {
        const cached = this.heightCache.get(item.id);
        if (cached && cached.source !== 'predicted') {
          measuredByData.set(item.data, cached);
        }
      }
    }
    this.skipNextCarry = false;

    this.items = items.map((item, index) => ({ data: item, id: cacheKeyFor(item?.id, index) }));

    this.heightCache.clear();
    for (const item of this.items) {
      const carried = measuredByData.get(item.data);
      // 'stale', not 'actual': the same reference may render at a new height.
      if (carried) {
        this.heightCache.set(item.id, { value: carried.value, source: 'stale' });
      }
    }

    if (this.viewport) {
      this.viewport.checkViewportSize();
    }
  }

  /**
   * Makes the next updateItems() start from predictions instead of carrying
   * measurements. For content swaps (new date range, changed filter) where item
   * references are reused but their rows — and so their heights — are replaced:
   * carried heights would pin off-screen items at the old content's heights.
   */
  dropMeasurementsOnNextUpdate() {
    this.skipNextCarry = true;
  }

  /**
   * Invalidates cached heights for in-place changes (e.g. expand/collapse).
   * Measurements demote to 'stale' rather than being dropped, so the total
   * never snaps back to predictions (which bounced the viewport at the bottom).
   */
  clearHeightCache() {
    for (const [id, cached] of this.heightCache) {
      if (cached.source === 'actual') {
        this.heightCache.set(id, { value: cached.value, source: 'stale' });
      } else if (cached.source === 'predicted') {
        // Predictor state may have changed (e.g. a card collapsed), so predictions recompute.
        this.heightCache.delete(id);
      }
    }

    if (this.viewport) {
      this.viewport.checkViewportSize();
    }
  }

  getItemHeight(item: VariableItem) {
    let height = 0;
    const cachedHeight = this.heightCache.get(item.id);
    if (!cachedHeight) {
      height = this.predictor.getItemHeight(item.data, this.viewport);
      this.heightCache.set(item.id, { value: height, source: 'predicted' });
    } else {

      height = cachedHeight.value;
    }

    return height;
  }

  measureItemsHeight(items: any[]): number {
    return items.map((item) => this.getItemHeight(item)).reduce((a, c) => a + c, 0);
  }

  getTotalHeight(): number {
    return this.measureItemsHeight(this.items);
  }

  getOffsetByItemId(id: number): number {
    return this.measureItemsHeight(this.items.slice(0, id));
  }

  getItemIndexByOffset(offset: number): number {
    let accumOffset = 0;

    for (let i = 0; i < this.items.length; i++) {
      accumOffset += this.getItemHeight(this.items[i]);;

      if (accumOffset >= offset) {
        return i;
      }
    }

    // Scrolled past the end of the accumulated content, which happens whenever
    // the total content size over-estimates. Falling back to 0 rendered the top
    // of the list at offset 0 while the viewport sat far below it, blanking the
    // page completely; clamping keeps the tail of the list on screen instead.
    return Math.max(0, this.items.length - 1);
  }

  getItemCountInViewport(startIndex: number): number {
    if (!this.viewport) {
      return 0;
    }

    let totalSize = this.predictor.getHeaderHeight?.() || 0;
    const viewportSize = this.viewport.getViewportSize();

    for (let i = startIndex; i < this.items.length; i++) {
      totalSize += this.getItemHeight(this.items[i]);

      if (totalSize >= viewportSize) {
        return i - startIndex + 1;
      }
    }

    return 0;
  }

  /**
   * Measure only once the browser has laid out the range we just asked for.
   * `updateRenderedRange` runs outside change detection, so measuring inline
   * reads the DOM of the *previous* render. Those heights were then stored as
   * `actual` and never re-measured, pinning the total content size to whatever
   * the list looked like one generation ago — the cards are a different height
   * by then, and the difference shows up as dead space past the last card.
   */
  private scheduleHeightMeasure() {
    if (typeof requestAnimationFrame !== 'function') {
      this.updateHeightCache();
      return;
    }

    if (this.pendingMeasure) {
      return;
    }

    this.pendingMeasure = true;
    requestAnimationFrame(() => {
      this.pendingMeasure = false;
      this.updateHeightCache();
    });
  }

  updateHeightCache() {
    if (!this.viewport || !this.elements) {
      return;
    }

    let cacheUpdated = false;
    for (const element of this.elements) {
      const id = cacheKeyFor(element?.id, -1);

      if (element?.id !== undefined && element?.id !== null && element?.id !== '') {
        const cachedHeight = this.heightCache.get(id);

        // Measure once per generation: getHeight() forces layout and this runs per rendered-range update.
        if (!cachedHeight || cachedHeight.source !== 'actual') {
          const height = element.getHeight();

          // A height of 0 means the item is not laid out yet — keep whatever we
          // have rather than recording an empty measurement as the truth.
          if (!height) { continue; }

          this.heightCache.set(id, { value: height, source: 'actual' });
          if (cachedHeight?.value !== height) {
            cacheUpdated = true;
          }
        }
      }
    }

    if (cacheUpdated) {
      this.viewport.setTotalContentSize(this.getTotalHeight());
    }
  }

  updateRenderedRange() {
    if (!this.viewport) {
      return;
    }

    const scrollOffset = this.viewport.measureScrollOffset();
    const scrollIndex = this.getItemIndexByOffset(scrollOffset);
    const dataLength = this.viewport.getDataLength();
    const renderedRange = this.viewport.getRenderedRange();
    const range = {
      start: renderedRange.start,
      end: renderedRange.end,
    };

    range.start = Math.max(0, scrollIndex - this.above);
    range.end = Math.min(
      dataLength,
      scrollIndex + this.getItemCountInViewport(scrollIndex) + this.below,
    );

    this.viewport.setRenderedRange(range);
    this.viewport.setRenderedContentOffset(
      this.getOffsetByItemId(range.start),
    );

    this.scrolledIndexChange$.next(scrollIndex);
    this.scheduleHeightMeasure();
  }
}

/**
 * @description The interface to predict the height of an item by using its data.
 * This is useful when the height of an item is not fixed and needs to be calculated dynamically.
 * The implementation of this interface should be provided to the `variable-strategy` directive.
 * @template T The type of the item data.
 * @function getItemHeight The function to predicts the height of an item.
 * @param item The item data that can be use to predict the height.
 * @param viewport The viewport of the virtual scroll component.
 * @returns The predicted height of the item in pixels.
 * @example
 * ```typescript
 * class FooTableItemSizePredictor implements ItemSizePredictor<Bar> {
 *  getItemHeight(itemData: Bar): number {
 *    const yPaddings = 32;
 *    const titleHeight = 30;
 *    const buttonHeight = 24;
 *    const buttonCount = itemData.baz.length; // assume that baz is an array of data represents the buttons on UI.
 *    const totalButtonHeight = buttonCount * buttonHeight; // in this case, one button is for one line.
 *
 *    return itemYPaddings + itemTitleHeight + totalButtonHeight;
 *  }
 * }
 * ```
 * @see `VariableVirtualScrollDirective`
 */
export interface ItemSizePredictor<T = any> {
  getItemHeight(item: T, viewport: CdkVirtualScrollViewport): number;
  getHeaderHeight?(): number;
}

class DefaultItemSizePredictor implements ItemSizePredictor {
  getItemHeight(): number {
    return 40;
  }
}

/**
 * @description The directive to provide the actual height of the item to the `VariableVirtualScrollStrategy` strategy.
 * This directive should be used with the `variable-strategy` directive to provide the actual height of each item.
 * @see `VariableVirtualScrollDirective`
 */
@Directive({
  selector: '[variable-item]',
})
export class VariableVirtualScrollItemDirective {
  // The id of the item will be used for managing and caching the height of the item.
  @Input('variable-item') id: string;

  constructor(private el: ElementRef) {}

  public getHeight() {
    const element = this.el.nativeElement as HTMLElement;
    // Vertical margins are part of the space an item occupies in the scroller,
    // and size predictors account for them, but getBoundingClientRect() reports
    // the border box and leaves them out. Measuring without them made the
    // measured and predicted heights of the same item describe different
    // quantities, so the total content size shifted by one margin per item as
    // items crossed between the two. Runs once per item per render generation —
    // the strategy caches the result as `actual`.
    const { marginTop, marginBottom } = getComputedStyle(element);
    const margins = (parseFloat(marginTop) || 0) + (parseFloat(marginBottom) || 0);

    return element.getBoundingClientRect().height + margins;
  }
}

/**
 * @description The directive to provide variable virtual scroll strategy for the `CdkVirtualScrollViewport` component.
 * This directive should be used with the `variable-item` directive to provide the ids of the item and calculates each item's
 * actual height later.
 * @see `VariableVirtualScrollItemDirective`
 */
@Directive({
  selector: '[variable-strategy]',
  providers: [
    {
      provide: VIRTUAL_SCROLL_STRATEGY,

      useFactory: (d: VariableVirtualScrollDirective) => d.scrollStrategy,
      deps: [forwardRef(() => VariableVirtualScrollDirective)],
    },
  ],
})
export class VariableVirtualScrollDirective {
  /**
   * The list of the `VariableVirtualScrollItemDirective` that will be used to calculate the actual height of each item.
   */
  @Input() set elements(elements: VariableVirtualScrollItemDirective[]) {
    this.scrollStrategy.elements = elements;
  };

  /**
   * The data list of the items that will be used to calculate the predicted heights of the items.
   */
  @Input() set items(items: any[]) {
    this.scrollStrategy.updateItems(items);
  };

  /**
   * The number of items that will be rendered above the current scroll position.
   */
  @Input() set above(value: number) {
    this.scrollStrategy.above = value;
  };

  /**
   * The number of items that will be rendered below the current scroll position.
   */
  @Input() set below(value: number) {
    this.scrollStrategy.below = value;
  };

  /**
   * The predictor to predict the height of an item.
   */
  @Input() set predictor(predictor: ItemSizePredictor) {
    this.scrollStrategy.predictor = predictor;
  };

  scrollStrategy: VariableVirtualScrollStrategy = new VariableVirtualScrollStrategy(new DefaultItemSizePredictor());
}
